/**
 * Content for the console orientation tour.
 *
 * This file is deliberately all data and no markup -- it is the file to edit when the wording
 * changes, and nothing here needs a developer to understand Vue or driver.js. The mechanism
 * that renders it lives in `@/composables/useConsoleTour`.
 *
 * Two audiences see the same steps with different wording, and a few steps only one of them needs:
 *
 *  - `relocation` -- people who worked in the Classic console and were moved here. They already
 *    know what Fareclock does; what they lost is where things are. Copy answers "where did it
 *    go", not "what is this".
 *  - `firstRun` -- people whose Fareclock login was created after the new console became the
 *    default (see CLASSIC_ERA_CUTOFF below), so they have never seen Classic. Telling them what
 *    "moved" is meaningless, and pointing them at the Classic console is actively unhelpful.
 *
 * Copy rules, learned the hard way from tours people skip:
 *  - One idea per step. If a step needs "and", it is two steps or it is one step too many.
 *  - Aim for 25 words or fewer in the body. Long steps get closed, not read.
 *  - Say what the person can do, not what we built. "Your timecards are here" beats
 *    "a consolidated attendance module with enhanced filtering".
 *  - Push detail into `learnMoreUrl` rather than into the body.
 *  - Write `{shortcut}` for the search modifier key; it resolves to Ctrl or the Mac glyph.
 *  - Headings need variant wording as often as bodies do. Anything phrased against the old
 *    console -- "new", "moved", "used to" -- needs a `firstRunTitle` or the step is relocation
 *    only, because a first login has nothing to compare against.
 */

/**
 * The date the new console became the default. Someone whose login predates this had the chance
 * to work in Classic and gets the relocation wording; anyone who signed up after it only ever
 * saw this console and gets the first-run wording.
 *
 * This is a proxy, not a fact -- someone who registered in August but first logged in this week
 * never saw Classic either. It is only used to pick tone, where being wrong costs a slightly odd
 * sentence, so the proxy is good enough. Do not use it to gate whether someone is helped at all.
 */
export const CLASSIC_ERA_CUTOFF = new Date('2026-09-01T00:00:00Z')

export type TourVariant = 'relocation' | 'firstRun'

export interface TourStepContent {
  /** Stable id, used for analytics and for tests. Do not renumber these when reordering. */
  id: string
  /** Matches a `data-tour="..."` attribute in the console shell. */
  target: string
  /** Heading for someone arriving from the Classic console. */
  title: string
  /** Heading for someone who has never seen Classic. Falls back to `title` when absent. */
  firstRunTitle?: string
  /** Wording for someone arriving from the Classic console. */
  body: string
  /** Wording for someone who has never seen Classic. Falls back to `body` when absent. */
  firstRunBody?: string
  /** Which side of the target the popover sits on. */
  side?: 'top' | 'right' | 'bottom' | 'left'
  align?: 'start' | 'center' | 'end'
  /** Optional deep link for people who want the full story. */
  learnMoreUrl?: string
  /** Variants this step applies to. Defaults to both. */
  variants?: TourVariant[]
}

export const CONSOLE_TOUR_STEPS: TourStepContent[] = [
  {
    id: 'nav',
    target: 'sidebar-nav',
    title: 'Sections are the new front doors',
    // "new" only means something next to an old console. To someone who never saw one it is
    // just a claim they cannot check, about a layout that is simply how the console is.
    firstRunTitle: 'Everything lives in this menu',
    body: 'The old Manage list is gone. Pick a section in the sidebar, then the page inside it \u2014 everything sits one level deeper.',
    firstRunBody: 'Three groups: Operations for day-to-day, HR Portal for people, Administration for everything else. Pick a section, then a page inside it.',
    side: 'right',
    align: 'start'
  },
  {
    // The single most-reported "where did it go". Naming the four pages in the title means
    // someone hunting for exactly one of them sees their word without reading a sentence, and
    // the one concrete instance teaches the sections-are-front-doors rule better than the
    // abstract version of it ever does.
    id: 'attendance',
    target: 'nav-attendance',
    title: 'Time Cards, Clock Logs, Raw Punches, Geo Mapping',
    body: 'All four moved in here, out of the old Manage list. Open Attendance first, then pick the one you want.',
    side: 'right',
    align: 'start',
    variants: ['relocation']
  },
  {
    // Renames hurt worse than moves. A moved page keeps its word, so searching still finds it;
    // a renamed one leaves the person typing a word that no longer exists anywhere.
    id: 'reports',
    target: 'nav-reports',
    title: 'Two things got renamed here',
    body: 'Memorized Reports are now My Saved Views. Scheduled Reports are now Delivery Schedules. Both are tabs inside Reports Center.',
    side: 'right',
    align: 'start',
    variants: ['relocation']
  },
  {
    id: 'overview',
    target: 'nav-overview',
    title: 'Start your day here',
    body: 'Overview shows the current pay period, open exceptions, and who is on the clock right now.',
    side: 'right',
    align: 'start',
    variants: ['firstRun']
  },
  {
    // After the steps on where things are, the way to bring the pages someone uses most back up to
    // one click. It closes out the sidebar steps so the tour crosses to the header once, rather
    // than swinging back to the sidebar after search. Personal, so it says whose sidebar changes;
    // an admin who fears rearranging everyone's console will not touch it.
    id: 'customize',
    target: 'sidebar-customize',
    title: 'Pin the pages you use most',
    body: 'Tired of opening Attendance to reach Time Cards? Add any page straight to your sidebar, grouped your way. Only you see the change.',
    firstRunBody: 'Add any page straight to your sidebar, and group them your way. Only you see the change.',
    side: 'right',
    align: 'end'
  },
  {
    // The relief after the rename step: having been told two things changed name, the person is
    // told they do not have to remember either, because the search still answers to the old
    // vocabulary (see `searchAliases` in OrgNav). Customize sits between them so the sidebar
    // steps stay together; the rename is still one step back, close enough to land.
    id: 'search',
    target: 'header-search',
    title: 'Can\u2019t find something?',
    body: 'Press {shortcut}K and type what you are after. Old names still work, so you do not have to memorize any of this.',
    firstRunBody: 'Press {shortcut}K and type a page name. Fastest way to reach any page in the console.',
    side: 'bottom',
    align: 'end'
  },
  {
    id: 'help',
    target: 'header-help',
    title: 'Help, including a human',
    body: 'Search the help docs, chat with support, send feedback, or book a 15-minute screen share.',
    side: 'bottom',
    align: 'end',
    // Relocation finishes on the completion card, which offers the same booking as a button.
    variants: ['firstRun']
  },
  {
    // Relocation only, and the title says why: this step exists because the control moved, not
    // because the avatar menu needs explaining. Someone signing up today almost certainly
    // belongs to one organization and has nothing to switch between, and their profile and
    // password are findable under their own avatar without being told.
    //
    // Whether they truly have a second organization is not knowable here -- the org list is
    // lazily loaded and `orgListLoaded` is still false on the dashboard -- and fetching it just
    // to decide a tour step is not worth the request.
    id: 'profile',
    target: 'header-profile',
    title: 'Switching organizations moved here',
    body: 'It used to be your company name at the top left. Now it is in this menu, with your profile and local settings.',
    side: 'bottom',
    align: 'end',
    variants: ['relocation']
  },
  {
    // Points at the avatar, not the theme row inside its dropdown: the menu is closed while the
    // tour runs, and the runner skips any step whose anchor is not on the page. Unlike the profile
    // step this is for everyone -- nobody goes looking for a theme they do not know exists, and the
    // picker no longer has a header button to stumble on.
    //
    // For relocation it directly follows `profile`, which lights up the same avatar, so its "too"
    // reads as the second half of one menu. First-run has no profile step and gets standalone copy.
    id: 'theme',
    target: 'header-profile',
    title: 'Light, dark, or in between',
    body: 'Your theme lives in this menu too. Pick one of five, or let it follow your computer\u2019s light or dark setting.',
    firstRunBody: 'Pick a theme from this menu, or let it follow your computer\u2019s light or dark setting.',
    side: 'bottom',
    align: 'end'
  },
  {
    id: 'classic',
    target: 'header-classic',
    title: 'Classic is still there',
    body: 'Not ready? Switch back anytime from here. Tell us why and we will help you settle in.',
    side: 'bottom',
    align: 'end',
    // A brand-new admin has no reason to be sent to the console we are retiring.
    variants: ['relocation']
  }
]

/** Copy for the consent card shown before anything is highlighted. */
export const TOUR_INVITATION = {
  relocation: {
    header: 'Welcome to your new console',
    title: 'Take the 60-second tour?',
    body: 'Same Fareclock, new layout. A few quick stops showing where your day-to-day work moved to.',
    accept: 'Show me around',
    decline: 'No thanks'
  },
  firstRun: {
    header: 'Welcome to Fareclock',
    title: 'Take the 60-second tour?',
    body: 'A few quick stops to show you where everything lives, so you are not hunting for it later.',
    accept: 'Show me around',
    decline: 'No thanks'
  }
} satisfies Record<TourVariant, Record<string, string>>

/**
 * The Help menu entry that reopens this tour.
 *
 * Shared rather than typed twice, because the completion card tells people this exact label is
 * how to get back here. The two drifting apart would send them hunting for a menu item that no
 * longer exists. Rename it freely -- both places follow.
 */
export const TOUR_MENU_LABEL = 'Take a tour'

/** Copy for the card shown after the last step. */
export const TOUR_COMPLETION = {
  header: 'That’s the tour',
  title: 'You’re all set',
  body: `Reopen this anytime from Help → ${TOUR_MENU_LABEL}. Want a person instead? Book a 15-minute walkthrough and we will screen-share.`,
  book: 'Book a walkthrough',
  finish: 'Done'
}

/** Mobile gets the offer but not the tour -- the nav is a drawer and the header collapses. */
export const TOUR_MOBILE_NOTICE =
  'The tour needs a wider screen. Open the console on a computer to take it, or book a walkthrough and we will show you.'

/**
 * Picks the wording variant for a person, given when their Fareclock login was created.
 *
 * Unknown date (the system pseudo-user, or a payload from before the field existed) falls back
 * to `relocation`: that wording makes sense to everyone, whereas first-run wording reads as
 * wrong to a Classic veteran.
 */
export function resolveTourVariant (userCreated: string | Date | null | undefined): TourVariant {
  if (!userCreated) {
    return 'relocation'
  }

  const created = userCreated instanceof Date ? userCreated : new Date(userCreated)
  if (Number.isNaN(created.getTime())) {
    return 'relocation'
  }

  return created >= CLASSIC_ERA_CUTOFF ? 'firstRun' : 'relocation'
}

/** The steps that apply to a variant, before DOM-presence filtering. */
export function stepsForVariant (variant: TourVariant): TourStepContent[] {
  return CONSOLE_TOUR_STEPS.filter(step => !step.variants || step.variants.includes(variant))
}

/**
 * The keyboard modifier for the page search, matching the badge rendered inside the search box
 * itself. Hardcoding the Mac glyph would have told every Windows admin to press a key their
 * keyboard does not have, in the one step whose whole point is rescuing people who are stuck.
 */
export function searchShortcutModifier (): string {
  return typeof navigator !== 'undefined' && navigator.userAgent.includes('Mac') ? '\u2318' : 'Ctrl'
}

/** The heading to show for a step in a given variant. */
export function titleForVariant (step: TourStepContent, variant: TourVariant): string {
  return variant === 'firstRun' && step.firstRunTitle ? step.firstRunTitle : step.title
}

/**
 * The body text to show for a step in a given variant, with `{shortcut}` resolved.
 */
export function bodyForVariant (step: TourStepContent, variant: TourVariant): string {
  const body = variant === 'firstRun' && step.firstRunBody ? step.firstRunBody : step.body
  return body.replace('{shortcut}', searchShortcutModifier())
}
